/**
 * WorkerMessenger.jsx - Buton floating + dialog pentru mesagerie angajat -> administrator
 */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, Send, AlertTriangle, HelpCircle, Info, X, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const messageTypes = [
  { value: 'error', label: 'Raportez o problemă', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'request', label: 'Solicit informații', icon: HelpCircle, color: 'text-blue-600' },
  { value: 'general', label: 'Mesaj general', icon: Info, color: 'text-slate-600' }
];

export default function WorkerMessenger({ userId, userName, companyId }) {
  const [showDialog, setShowDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [messageType, setMessageType] = useState('general');
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  
  useEffect(() => {
    if (showDialog && userId) {
      fetchMessages();
    }
  }, [showDialog, userId]);
  
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages/worker/${userId}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async () => {
    if (!subject.trim() || !messageText.trim()) {
      toast.error("Completează subiectul și mesajul");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        sender_id: userId,
        sender_name: userName,
        sender_role: 'worker',
        company_id: companyId,
        subject: subject,
        message: messageText,
        message_type: messageType
      });
      
      toast.success("Mesaj trimis către administrator!");
      setSubject('');
      setMessageText('');
      setMessageType('general');
      fetchMessages();
    } catch (error) {
      toast.error("Eroare la trimiterea mesajului");
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };
  
  const getTypeIcon = (type) => {
    const found = messageTypes.find(t => t.value === type);
    if (!found) return Info;
    return found.icon;
  };
  
  const getTypeColor = (type) => {
    const found = messageTypes.find(t => t.value === type);
    return found?.color || 'text-slate-600';
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'unread': return <Badge className="bg-yellow-500 text-xs">Necitit</Badge>;
      case 'read': return <Badge className="bg-blue-500 text-xs">Citit</Badge>;
      case 'replied': return <Badge className="bg-green-500 text-xs">Răspuns</Badge>;
      default: return null;
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  return (
    <>
      {/* Message Button - inline, positioned under employee name */}
      <button
        onClick={() => setShowDialog(true)}
        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
        data-testid="worker-messenger-button"
        title="Contactează Administratorul"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        <span>Mesaje</span>
      </button>
      
      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="worker-messenger-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <MessageCircle className="w-5 h-5 text-indigo-600" />
              Contactează Administratorul
            </DialogTitle>
            <DialogDescription>
              Trimite mesaje, raportează probleme sau solicită informații
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* New Message Form */}
            <Card className="border-indigo-200 bg-indigo-50">
              <CardContent className="p-4 space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Tip mesaj</Label>
                  <Select value={messageType} onValueChange={setMessageType}>
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {messageTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-semibold">Subiect</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ex: Întrebare despre pontaj..."
                    className="bg-white"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-semibold">Mesaj</Label>
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Scrie mesajul tău aici..."
                    rows={3}
                    className="bg-white resize-none"
                  />
                </div>
                
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !subject.trim() || !messageText.trim()}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {sending ? "Se trimite..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Trimite Mesajul
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Message History Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between"
            >
              <span>Istoricul mesajelor ({messages.length})</span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            {/* Message History */}
            {showHistory && (
              <div className="space-y-3">
                {loading ? (
                  <p className="text-center text-slate-500 py-4">Se încarcă...</p>
                ) : messages.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">Nu ai trimis încă niciun mesaj</p>
                ) : (
                  messages.map(msg => {
                    const TypeIcon = getTypeIcon(msg.message_type);
                    return (
                      <Card key={msg.id} className="border-slate-200">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <TypeIcon className={`w-4 h-4 ${getTypeColor(msg.message_type)}`} />
                              <span className="font-semibold text-sm">{msg.subject}</span>
                            </div>
                            {getStatusBadge(msg.status)}
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-2">{msg.message}</p>
                          <p className="text-xs text-slate-400">{formatDate(msg.created_at)}</p>
                          
                          {/* Replies */}
                          {msg.replies && msg.replies.length > 0 && (
                            <div className="mt-3 pl-3 border-l-2 border-green-400 space-y-2">
                              {msg.replies.map(reply => (
                                <div key={reply.id} className="bg-green-50 rounded-lg p-2">
                                  <p className="text-xs font-semibold text-green-700">
                                    Răspuns de la {reply.replier_name}:
                                  </p>
                                  <p className="text-sm text-slate-700">{reply.text}</p>
                                  <p className="text-xs text-slate-400 mt-1">{formatDate(reply.created_at)}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
